import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { FolderSummary, ProjectView } from '@vwt/contracts';
import { Button, Dialog, DialogContent, DialogFooter, Field, NativeSelect, Skeleton } from '@vwt/ui';
import { FileText, FolderOpen, Pencil } from 'lucide-react';
import { useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { SectionTitle } from '../../components/common.js';

/**
 * The documents that link to a record (ADR-0040 §7): page and task chips, @mentions and internal
 * links. Shown on work items, requests, projects, teams and documents; hidden when there are none.
 */
export function DocumentBacklinks({ targetKind, targetId, title }: { targetKind: 'DOCUMENT' | 'WORK_ITEM' | 'REQUEST' | 'PROJECT' | 'TEAM' | 'USER'; targetId: string; title?: string }) {
  const { t } = useTranslation();
  const links = useApiQuery('listDocumentBacklinks', { query: { targetKind, targetId } }, { staleTime: 30_000 });
  const docs = links.data?.data ?? [];
  if (links.isLoading) return <Skeleton className="h-10" />;
  if (!docs.length) return null;
  return (
    <section aria-label={title ?? t('documents.backlinks.title')}>
      <SectionTitle>{title ?? t('documents.backlinks.title')}</SectionTitle>
      <ul className="flex flex-col gap-1">
        {docs.map((d) => (
          <li key={d.id}>
            <Link to={`/docs/${d.id}`} className="flex min-w-0 items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted">
              {d.icon ? <span aria-hidden>{d.icon}</span> : <FileText aria-hidden className="size-3.5 shrink-0 text-fg-subtle" />}
              <span className="min-w-0 flex-1 truncate">{d.title}</span>
              <span className="hidden max-w-40 shrink-0 truncate text-xs text-fg-subtle sm:inline">{d.path}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Folders as "Parent / Child" labels in tree order, for pickers. */
function folderOptions(folders: FolderSummary[]): Array<{ id: string; label: string }> {
  const byParent = new Map<string | null, FolderSummary[]>();
  for (const f of folders) byParent.set(f.parentId, [...(byParent.get(f.parentId) ?? []), f]);
  const out: Array<{ id: string; label: string }> = [];
  const walk = (parentId: string | null, prefix: string) => {
    for (const f of (byParent.get(parentId) ?? []).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))) {
      const label = `${prefix}${f.icon ? `${f.icon} ` : ''}${f.name}`;
      out.push({ id: f.id, label });
      walk(f.id, `${label} / `);
    }
  };
  walk(null, '');
  return out;
}

/** The project's documentation folder (ADR-0040 §7); its managers choose or clear it. */
export function ProjectDocumentation({ project }: { project: ProjectView }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const canEdit = project.availableActions.includes('edit');
  const folder = project.documentationFolder;
  if (!folder && !canEdit) return null;
  return (
    <div>
      <div className="flex items-center justify-between">
        <SectionTitle>{t('documents.projectFolder.title')}</SectionTitle>
        {canEdit ? (
          <Button variant="ghost" size="iconSm" aria-label={t('documents.projectFolder.choose')} onClick={() => setEditing(true)}>
            <Pencil />
          </Button>
        ) : null}
      </div>
      {folder ? (
        <Link to={`/docs/folders/${folder.id}`} className="flex items-center gap-1.5 text-sm hover:underline">
          {folder.icon ? <span aria-hidden>{folder.icon}</span> : <FolderOpen aria-hidden className="size-3.5 text-fg-subtle" />}
          {folder.name}
        </Link>
      ) : (
        <p className="text-xs text-fg-subtle">{t('documents.projectFolder.none')}</p>
      )}
      {editing ? <ProjectFolderDialog project={project} onClose={() => setEditing(false)} /> : null}
    </div>
  );
}

function ProjectFolderDialog({ project, onClose }: { project: ProjectView; onClose: () => void }) {
  const { t } = useTranslation();
  const folders = useApiQuery('listDocumentFolders', {});
  const options = useMemo(() => folderOptions(folders.data?.data ?? []), [folders.data]);
  const [value, setValue] = useState(project.documentationFolder?.id ?? '');
  const save = useApiMutation('updateProject', { invalidate: ['getProject', 'listProjects'] });
  const submit = async () => {
    try {
      await save.mutateAsync({ params: { id: project.id }, body: { expectedVersion: project.version, documentationFolderId: value || null } });
      onClose();
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('documents.projectFolder.choose')} description={t('documents.projectFolder.hint')}>
        <Field label={t('documents.projectFolder.folder')}>
          <NativeSelect value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">{t('documents.projectFolder.noneOption')}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" loading={save.isPending} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
